/*  Shared  */
import { Response } from 'application/Shared/Helpers';
import { debug } from 'application/Shared/Utils';
import { Calculation } from 'application/Shared/types/calculation';

/*  NPM  */
import axios from 'axios';

/*  React  */
import { useEffect, useMemo, useState } from 'react';

interface UserCalculations {
    weekly_users?: number;
}

interface CalculationResponseData {
    data: Calculation[];
    message: string;
    serverResponse: number;
}

// Hook to fetch the /calculation response
const defaultObject = {};
// Always define the params object outside the component using it, otherwise
// you will keep rendering and re-rendering the component.
export const useFetchCalculation = ({
    clientId = null,
    params = defaultObject
}: {
    clientId?: string | null | undefined;
    params?: Record<string, string | boolean>;
}) => {
    const [calculations, setCalculations] = useState({} as UserCalculations);
    const [calculationDates, setCalculationDates] = useState(
        {} as UserCalculations
    );
    const [loading, setLoading] = useState(true);
    // calling refetchData will force a rerender on the hook
    const [shouldRefetchData, setShouldRefetch] = useState({});

    const refetchData = () => setShouldRefetch({});

    const parameters = useMemo(
        () =>
            clientId
                ? {
                      ...params,
                      includeClients: true,
                      platformTenantId: clientId
                  }
                : params,
        [clientId, params]
    );

    useEffect(() => {
        const { CancelToken } = axios;
        const source = CancelToken.source();

        const fetchData = async () => {
            setLoading(true);
            try {
                const response = await axios.get<CalculationResponseData>(
                    '/calculation',
                    {
                        cancelToken: source.token,
                        params: parameters
                    }
                );
                if (!Response.hasData(response)) {
                    debug.e('No calculation found.', response);
                } else {
                    const allData = Response.getData(response);
                    const calculationsTmp = allData.reduce<
                        Record<string, string | string[]>
                    >((acc, d) => {
                        acc[d.linkedType] = d.data;
                        return acc;
                    }, {});

                    const calculationDatesTmp = allData.reduce<
                        Record<string, Date>
                    >((acc, d) => {
                        acc[d.linkedType] = d.dateUpdated;
                        return acc;
                    }, {});

                    setCalculations(calculationsTmp);
                    setCalculationDates(calculationDatesTmp);
                }
            } catch (error) {
                debug.e('Unable to get calculation.', error);
            }
            setLoading(false);
        };
        fetchData();

        return () => {
            // Cancel the request when the component unmounts
            source.cancel();
        };
    }, [clientId, parameters, shouldRefetchData]);

    return {
        calculationDates,
        calculations,
        loading,
        refetchData
    };
};
